import * as Cesium from "cesium";
import type React from "react";
import { useEffect, useState } from "react";
import { useGlobalState } from "senf-shared";
import {
  getSimpleGeoidUndulation,
  setGlobalManualOffset,
} from "./cesium/cesium-geoid";
import {
  setOsmBuildingsColor,
  toggleOsmBuildings,
} from "./cesium/cesium-imagery";
import type { ImageryStyle } from "./cesium/cesium-types";
import SettingsPanel from "./cesium/components/SettingsPanel";

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface BimModel {
  assetId: number;
  assetName: string;
  detectedEpsg: string;
  fileSize: string;
  uploadDate: Date;
  styles?: StyleCondition[];
}

interface CesiumControlsProps {
  bimModels: BimModel[];
  onBimModelsChange: (models: BimModel[]) => void;
  imageryStyle: ImageryStyle;
  onImageryStyleChange: (style: ImageryStyle) => void;
  polygonDrawingEnabled?: boolean;
  onPolygonDrawingChange?: (enabled: boolean) => void;
  osmBuildingsEnabled: boolean;
  onOsmBuildingsChange: (enabled: boolean) => void;
  osmBuildingColor: string;
  onOsmBuildingColorChange: (color: string, opacity: number) => void;
  osmBuildingOpacity: number;
  geoidHeightDifference?: number | null;
  onGeoidHeightDifferenceChange?: (height: number | null) => void;
}

const CesiumControls: React.FC<CesiumControlsProps> = ({
  bimModels,
  onBimModelsChange,
  imageryStyle,
  onImageryStyleChange,
  polygonDrawingEnabled = false,
  onPolygonDrawingChange,
  osmBuildingsEnabled,
  onOsmBuildingsChange,
  osmBuildingColor,
  onOsmBuildingColorChange,
  osmBuildingOpacity,
  geoidHeightDifference: propGeoidHeightDifference,
  onGeoidHeightDifferenceChange,
}) => {
  const [statefulCesiumMap] = useGlobalState().statefulCesiumMap;
  const viewer = statefulCesiumMap?.viewer;
  const [localGeoidHeightDifference, setLocalGeoidHeightDifference] =
    useState<number>(0);

  // Use prop value if provided, otherwise use local state
  const geoidHeightDifference =
    propGeoidHeightDifference ?? localGeoidHeightDifference;

  // Debug logging
  useEffect(() => {
    console.log("CesiumControls geoidHeightDifference:", {
      propValue: propGeoidHeightDifference,
      localValue: localGeoidHeightDifference,
      finalValue: geoidHeightDifference,
    });
  }, [
    propGeoidHeightDifference,
    localGeoidHeightDifference,
    geoidHeightDifference,
  ]);

  // Calculate geoid height difference based on current camera position
  useEffect(() => {
    if (viewer && !propGeoidHeightDifference) {
      const calculateGeoidHeight = () => {
        try {
          const camera = viewer.camera;
          const position = camera.position;

          // Convert camera position to cartographic coordinates
          const cartographic =
            viewer.scene.globe.ellipsoid.cartesianToCartographic(position);
          if (cartographic) {
            const lonDeg = cartographic.longitude * (180 / Math.PI);
            const latDeg = cartographic.latitude * (180 / Math.PI);

            // Calculate geoid undulation for current location
            const undulation = getSimpleGeoidUndulation(lonDeg, latDeg);
            setLocalGeoidHeightDifference(undulation);
          }
        } catch (error) {
          console.warn("Could not calculate geoid height difference:", error);
          // Fallback to default value
          setLocalGeoidHeightDifference(50);
        }
      };

      // Calculate initial height difference
      calculateGeoidHeight();

      // Listen for camera changes to update height difference
      const handleCameraMove = () => {
        calculateGeoidHeight();
      };

      viewer.camera.moveEnd.addEventListener(handleCameraMove);

      return () => {
        viewer.camera.moveEnd.removeEventListener(handleCameraMove);
      };
    }
  }, [viewer, propGeoidHeightDifference]);

  // Handle geoid height difference changes
  const handleGeoidHeightDifferenceChange = (height: number | null) => {
    // Update local state if no prop handler is provided
    if (!onGeoidHeightDifferenceChange) {
      setLocalGeoidHeightDifference(height || 0);
    }

    // Apply the new geoid offset to the viewer
    if (viewer) {
      if (height === null) {
        // Revert to automatic calculation - clear the manual offset
        setGlobalManualOffset(0);

        // Recalculate the automatic height difference for current location
        try {
          const camera = viewer.camera;
          const position = camera.position;

          const cartographic =
            viewer.scene.globe.ellipsoid.cartesianToCartographic(position);
          if (cartographic) {
            const lonDeg = cartographic.longitude * (180 / Math.PI);
            const latDeg = cartographic.latitude * (180 / Math.PI);

            const automaticUndulation = getSimpleGeoidUndulation(
              lonDeg,
              latDeg,
            );

            // Apply the automatic offset to all existing tilesets
            const tilesets = viewer.scene.primitives._primitives.filter(
              (primitive: any) => primitive instanceof Cesium.Cesium3DTileset,
            );

            tilesets.forEach((tileset: any) => {
              try {
                const centerCartesian = tileset.boundingSphere.center;
                if (centerCartesian) {
                  const upVector = Cesium.Cartesian3.normalize(
                    centerCartesian,
                    new Cesium.Cartesian3(),
                  );
                  const offsetVector = Cesium.Cartesian3.multiplyByScalar(
                    upVector,
                    automaticUndulation,
                    new Cesium.Cartesian3(),
                  );

                  const translationMatrix =
                    Cesium.Matrix4.fromTranslation(offsetVector);
                  tileset.modelMatrix = Cesium.Matrix4.clone(translationMatrix);
                }
              } catch (error) {
                console.warn(
                  "Could not apply automatic geoid offset to tileset:",
                  error,
                );
              }
            });
          }
        } catch (error) {
          console.warn(
            "Could not calculate automatic geoid height difference:",
            error,
          );
        }
      } else {
        // Calculate the difference between old and new offset
        const oldOffset = geoidHeightDifference || 0;
        const offsetDifference = height - oldOffset;

        setGlobalManualOffset(height);

        // Apply the offset difference to all existing tilesets
        const tilesets = viewer.scene.primitives._primitives.filter(
          (primitive: any) => primitive instanceof Cesium.Cesium3DTileset,
        );

        tilesets.forEach((tileset: any) => {
          try {
            // Get the tileset center for applying the offset
            const centerCartesian = tileset.boundingSphere.center;
            if (centerCartesian) {
              const upVector = Cesium.Cartesian3.normalize(
                centerCartesian,
                new Cesium.Cartesian3(),
              );
              const offsetVector = Cesium.Cartesian3.multiplyByScalar(
                upVector,
                offsetDifference,
                new Cesium.Cartesian3(),
              );

              // Create a translation matrix for the offset difference
              const translationMatrix =
                Cesium.Matrix4.fromTranslation(offsetVector);

              // Apply the offset difference to the existing model matrix
              tileset.modelMatrix = Cesium.Matrix4.multiply(
                translationMatrix,
                tileset.modelMatrix,
                new Cesium.Matrix4(),
              );
            }
          } catch (error) {
            console.warn("Could not apply geoid offset to tileset:", error);
          }
        });
      }

      // Dispatch event to notify other components about the change
      window.dispatchEvent(
        new CustomEvent("geoidHeightChange", {
          detail: { height },
        }),
      );
    }

    // Call the prop handler if provided
    onGeoidHeightDifferenceChange?.(height);
  };

  // Collect all style conditions from all assets
  const getAllStyleConditions = (): StyleCondition[] => {
    return bimModels.flatMap((model) => model.styles || []);
  };

  // Apply initial styles when component mounts or when bimModels change
  useEffect(() => {
    const initialConditions = bimModels.flatMap((model) => model.styles || []);
    if (initialConditions.length > 0) {
      // Dispatch initial styles with a small delay to ensure Cesium is ready
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("styleConditionsChange", {
            detail: { conditions: initialConditions },
          }),
        );
      }, 100);
    }
  }, [bimModels]);

  const handleDepthTestChange = (enabled: boolean) => {
    window.dispatchEvent(
      new CustomEvent("depthTestChange", { detail: { enabled } }),
    );
  };

  const handleShadowsChange = (enabled: boolean) => {
    window.dispatchEvent(
      new CustomEvent("shadowsChange", { detail: { enabled } }),
    );
  };

  const handlePolygonDrawingChange = (enabled: boolean) => {
    if (onPolygonDrawingChange) {
      onPolygonDrawingChange(enabled);
    }
    // You might also want to dispatch a global event if other parts of the app need to know
    window.dispatchEvent(
      new CustomEvent("polygonDrawingChange", { detail: { enabled } }),
    );
  };

  const handleOsmBuildingsChange = (enabled: boolean) => {
    onOsmBuildingsChange(enabled);
    if (viewer) {
      // Create mapData object with current settings - needed for both enable and disable
      const mapData = {
        osmBuildingsEnabled: enabled,
        osmBuildingColor: osmBuildingColor,
        osmBuildingOpacity: osmBuildingOpacity,
      };
      toggleOsmBuildings(viewer, enabled, mapData);
    }
  };

  const handleOsmBuildingColorChange = (color: string, opacity: number) => {
    onOsmBuildingColorChange(color, opacity);
    if (viewer) {
      setOsmBuildingsColor(viewer, color, opacity);
    }
  };

  const findAssetForProperty = (
    property: string,
    attributes: { [key: string]: any },
  ): BimModel | null => {
    // Try to find the asset that contains this property
    // For now, we'll use a simple heuristic but this could be improved
    // by analyzing the property structure or asset metadata

    // Check if we can match by asset name or other identifying features
    const propertyString = property.toLowerCase();

    for (const model of bimModels) {
      // If the property name contains any part of the asset name, assign it to that asset
      if (
        propertyString.includes(model.assetName.toLowerCase()) ||
        model.assetName.toLowerCase().includes(propertyString)
      ) {
        return model;
      }
    }

    // If no specific match found, assign to the first asset
    return bimModels[0] || null;
  };

  const handleStyleConditionsChange = (conditions: StyleCondition[]) => {
    // Distribute conditions to appropriate assets based on property analysis
    const updatedModels: BimModel[] = bimModels.map((model) => ({
      ...model,
      styles: [] as StyleCondition[],
    }));

    // Group conditions by asset
    conditions.forEach((condition) => {
      const targetAsset = findAssetForProperty(condition.property, {});
      if (targetAsset) {
        const modelIndex = updatedModels.findIndex(
          (m) => m.assetId === targetAsset.assetId,
        );
        if (modelIndex >= 0 && updatedModels[modelIndex].styles) {
          updatedModels[modelIndex].styles!.push(condition);
        }
      }
    });

    // If no asset was found for some conditions, put them on the first asset
    const orphanConditions = conditions.filter((condition) => {
      return !updatedModels.some(
        (model) =>
          model.styles &&
          model.styles.some(
            (s) =>
              s.property === condition.property && s.value === condition.value,
          ),
      );
    });

    if (
      orphanConditions.length > 0 &&
      updatedModels.length > 0 &&
      updatedModels[0].styles
    ) {
      updatedModels[0].styles.push(...orphanConditions);
    }

    onBimModelsChange(updatedModels);

    window.dispatchEvent(
      new CustomEvent("styleConditionsChange", {
        detail: { conditions },
      }),
    );
  };

  return (
    <SettingsPanel
      onDepthTestChange={handleDepthTestChange}
      onShadowsChange={handleShadowsChange}
      onPolygonDrawingChange={handlePolygonDrawingChange}
      imageryStyle={imageryStyle}
      onImageryStyleChange={onImageryStyleChange}
      conditions={getAllStyleConditions()}
      onStyleConditionsChange={handleStyleConditionsChange}
      osmBuildingsEnabled={osmBuildingsEnabled}
      onOsmBuildingsChange={handleOsmBuildingsChange}
      osmBuildingColor={osmBuildingColor}
      onOsmBuildingColorChange={handleOsmBuildingColorChange}
      osmBuildingOpacity={osmBuildingOpacity}
      geoidHeightDifference={geoidHeightDifference}
      onGeoidHeightDifferenceChange={handleGeoidHeightDifferenceChange}
    />
  );
};

export default CesiumControls;
